'use client';
import { Moon, Sun } from 'lucide-react';
import { useSkin } from '../../lib/skinContext';
import { catalogueEntry, skinDisplayName } from '../../lib/skins';
import { useTranslation } from '../../lib/i18n';
import { Button } from './Button';

/** Cycles the interface through the skins the instance allows. Deliberately a single button and not a menu,
 *  unlike the language switcher beside it: a skin is judged by looking at it, so the fastest way to compare
 *  two is to flip between them, and a dropdown puts a list of names in the way of that. The names are still
 *  visible — the admin list in Settings is where they are chosen.
 *
 *  The icon follows the design's own colour scheme rather than a skin id, so a customer skin shows the moon
 *  when it is dark and the sun when it is light, whatever the operator named it.
 *
 *  Renders NOTHING when fewer than two choices are allowed. A control with one option is not a choice, and an
 *  instance that has not enabled switching should look exactly as it did before this existed. */
export function SkinSwitcher({ collapsed = false, placement = 'topbar' }: { collapsed?: boolean; placement?: 'topbar' | 'drawer' }) {
  const { t } = useTranslation();
  const { choice, allowed, catalogue, switching, cycle } = useSkin();

  if (allowed.length < 2) return null;

  const name = skinDisplayName(t, choice, catalogue);
  const label = `${t.common.skin}: ${name}`;
  const dark = catalogueEntry(catalogue, choice)?.colorScheme === 'dark';
  const Icon = dark ? Moon : Sun;

  return (
    <Button
      type="button"
      variant="ghost"
      // The shared button scale, not a box of this component's own: the icon form is the shell's default
      // control step (36px, 44px on a coarse pointer) and the labelled form beside a row's text is the
      // same default with its label.
      size={collapsed ? 'icon' : 'default'}
      onClick={() => { void cycle(); }}
      // Disabled, not merely busy: a second press while the chosen design's stylesheet is in flight would
      // ask for a design the switcher is not showing yet.
      disabled={switching}
      aria-busy={switching}
      aria-label={label}
      title={`${label} — ${t.common.skinCycle}`}
      className={`skin-switcher__button text-muted-foreground hover:text-foreground ${collapsed ? 'justify-center rounded-md px-0' : 'gap-1.5 rounded-full'}`}
    >
      <Icon size={18} strokeWidth={1.5} aria-hidden />
      {/* The name is the only way to tell two dark skins apart at a glance. A top bar may hide it until
          there is room; the drawer explicitly owns a labelled row and must not inherit that viewport rule.
          `collapsed` is the icon-only top-bar form, where the title and aria-label still name the skin. */}
      {collapsed ? null : (
        <span className={`skin-switcher__name text-eyebrow uppercase ${placement === 'drawer' ? '' : 'hidden lg:inline'}`}>
          {name}
        </span>
      )}
    </Button>
  );
}
